// Settings save (settings-save): the app-wide half of the unsaved-changes warning. Pages say they
// have unsaved settings with useUnsavedChanges (lib/unsaved-changes.ts; SettingsSaveBar does it);
// this holds the one react-router blocker for all of them (a router honours only one blocker).
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { Outlet, useBlocker, type BlockerFunction } from 'react-router';
import { UnsavedContext, type RegisterUnsaved } from '@/lib/unsaved-changes';
import { useConfirm } from './confirm';

/**
 * Mount once, inside the router and the ConfirmProvider (the app's root route does). Renders its
 * children, or the route's Outlet. Navigating to another page (or tab of a page) while anything
 * registered with useUnsavedChanges is dirty asks "Discard your changes?" first.
 */
export function UnsavedChangesGuard({ children }: { children?: ReactNode }) {
  const confirm = useConfirm();
  // A ref, not state: registration happens as the page commits, so a click straight after Save
  // already sees the page clean (state would reach the blocker one effect later).
  const dirty = useRef(new Set<string>());
  const register = useCallback<RegisterUnsaved>((id, on) => {
    if (on) dirty.current.add(id);
    else dirty.current.delete(id);
  }, []);
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => dirty.current.size > 0 && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
    [],
  );
  const blocker = useBlocker(shouldBlock);
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    void confirm({ title: 'Discard your changes?', description: 'You have changes that are not saved. Leave this page without saving them?', confirmLabel: 'Discard changes', danger: true }).then((ok) =>
      ok ? blocker.proceed() : blocker.reset(),
    );
  }, [blocker, confirm]);
  return <UnsavedContext.Provider value={register}>{children ?? <Outlet />}</UnsavedContext.Provider>;
}
